import type { LedgerCollapse } from "./types";

/**
 * One collapse point per screen, driven by container queries rather than
 * viewport breakpoints: the ledger reacts to the width it is given, not the
 * width of the window (side panels and sidebars change one without the other).
 *
 * The portal version returned MUI `sx` objects (emotion compiled them at
 * runtime). Here the same runtime-compiled mechanism survives the port: each
 * helper returns a class name, and the matching `@container` rule is injected
 * once into a document-level `<style>` tag — Tailwind cannot generate named
 * container variants for container names and widths that only exist at call
 * time, and the names/widths are the screens' data, not ours. Native CSS
 * nesting carries the `@container` block inside each rule.
 */

const STYLE_TAG_ID = "ds-ledger-container-queries";

/** Classes already injected into the document — one rule per class, ever. */
const emittedClasses = new Set<string>();

/** Class-name-safe stand-in for arbitrary container names. */
function slug(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, "-");
}

function appendCss(css: string): void {
  if (typeof document === "undefined") {
    return;
  }
  let tag = document.getElementById(STYLE_TAG_ID);
  if (tag === null) {
    tag = document.createElement("style");
    tag.id = STYLE_TAG_ID;
    document.head.appendChild(tag);
  }
  tag.appendChild(document.createTextNode(`\n${css}`));
}

/**
 * Emit `css` for `className` once. Safe on the server (returns the class;
 * the rule is only injected in a real document) and idempotent in HMR storms.
 */
function emitRule(className: string, css: string): string {
  if (emittedClasses.has(className)) {
    return className;
  }
  emittedClasses.add(className);
  appendRule(className, css);
  return className;
}

function appendRule(className: string, css: string): void {
  const escaped = escapeSelector(className);
  appendCss(`${escaped} {${css}}`);
}

function escapeSelector(className: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return `.${CSS.escape(className)}`;
  }
  return `.${className}`;
}

/** The raw text of a rule body, shared by the hide/show emitters. */
function containerRuleCss(
  container: string,
  px: number,
  inside: string,
): string {
  return `@container ${container} (max-width: ${px}px) { ${inside} }`;
}

/** Style the element that owns the query context. Screens apply this once. */
export function ledgerContainerClass(container: string): string {
  const className = `ds-ledger-c-${slug(container)}`;
  return emitRule(
    className,
    `container-type: inline-size; container-name: ${container};`,
  );
}

export function containerMaxWidthQuery(container: string, px: number): string {
  return `@container ${container} (max-width: ${px}px)`;
}

/** Hide at or below an explicit width inside the named container. */
export function hideBelowWidthClass(container: string, px: number): string {
  const className = `ds-ledger-hide-${slug(container)}-${px}`;
  return emitRule(className, containerRuleCss(container, px, "display: none;"));
}

/** Show only at or below an explicit width inside the named container. */
export function showBelowWidthClass(
  container: string,
  px: number,
  display = "block",
): string {
  const className = `ds-ledger-show-${slug(container)}-${px}-${display}`;
  const query = containerRuleCss(container, px, `display: ${display};`);
  return emitRule(className, `display: none; ${query}`);
}

/** Hide at or below the screen's collapse point (columns, band totals). */
export function hideBelowClass(collapse: LedgerCollapse): string {
  return hideBelowWidthClass(collapse.container, collapse.at);
}

/** Show only at or below the collapse point (the row meta line). */
export function showBelowClass(
  collapse: LedgerCollapse,
  display = "block",
): string {
  return showBelowWidthClass(collapse.container, collapse.at, display);
}

/** Tighten a row once its secondary columns collapse into the meta line. */
export function compactRowBelowClass(collapse: LedgerCollapse): string {
  const className = `ds-ledger-row-compact-${slug(collapse.container)}-${collapse.at}`;
  return emitRule(
    className,
    containerRuleCss(
      collapse.container,
      collapse.at,
      "align-items: flex-start; gap: 0.5rem; padding-top: 0.75rem; padding-bottom: 0.75rem;",
    ),
  );
}

/** Let the value use only the width it needs after secondary columns hide. */
export function compactValueBelowClass(collapse: LedgerCollapse): string {
  const className = `ds-ledger-value-compact-${slug(collapse.container)}-${collapse.at}`;
  return emitRule(
    className,
    containerRuleCss(
      collapse.container,
      collapse.at,
      "width: auto; max-width: 42%;",
    ),
  );
}
